import Delta from 'quill-delta';
import { ReactElement, ReactNode } from 'react';

type InlineAttrs = {
	bold?: boolean;
	italic?: boolean;
	underline?: boolean;
	link?: string;
	size?: string;
};

type BlockAttrs = {
	header?: number | string;
	list?: string;
	align?: string;
	indent?: number;
	direction?: string;
};

type InlineToken =
	| { type: 'text'; text: string; attrs: InlineAttrs }
	| { type: 'image'; url: string }
	| { type: 'video'; url: string }
	| { type: 'unknown' };

type Block = {
	attrs: BlockAttrs;
	inline: InlineToken[];
};

const SAFE_LINK_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];
const SAFE_EMBED_SCHEMES = ['http:', 'https:'];

function isSafeUrl(raw: unknown, allowedSchemes: string[]): raw is string {
	if (typeof raw !== 'string' || raw.length === 0) {
		return false;
	}
	// Relative URLs (no scheme) are allowed; they can never be javascript: etc.
	if (raw.startsWith('/') || raw.startsWith('#') || raw.startsWith('?')) {
		return true;
	}
	try {
		const parsed = new URL(raw, 'http://placeholder-base/');
		// If the input was relative, URL() resolves against base so scheme becomes http:.
		// Detect relative by checking the raw string has no scheme.
		if (!/^[a-z][a-z0-9+.-]*:/i.test(raw)) {
			return true;
		}
		return allowedSchemes.includes(parsed.protocol.toLowerCase());
	} catch {
		return false;
	}
}

function parseOps(ops: Delta['ops']): { blocks: Block[]; trailing: InlineToken[] } {
	const blocks: Block[] = [];
	let current: InlineToken[] = [];

	for (const op of ops) {
		const insert = op.insert;
		const attributes = (op.attributes ?? {}) as InlineAttrs & BlockAttrs;

		if (typeof insert === 'string') {
			// Split on newlines. Each newline closes the current block with the op's
			// block-level attributes applied to that block.
			let remaining = insert;
			while (remaining.length > 0) {
				const nlIndex = remaining.indexOf('\n');
				if (nlIndex === -1) {
					if (remaining.length > 0) {
						current.push({ type: 'text', text: remaining, attrs: attributes });
					}
					break;
				}
				const before = remaining.slice(0, nlIndex);
				if (before.length > 0) {
					current.push({ type: 'text', text: before, attrs: attributes });
				}
				blocks.push({ attrs: attributes, inline: current });
				current = [];
				remaining = remaining.slice(nlIndex + 1);
			}
		} else if (insert && typeof insert === 'object') {
			const embed = insert as Record<string, unknown>;
			if (typeof embed.image === 'string') {
				current.push({ type: 'image', url: embed.image });
			} else if (typeof embed.video === 'string') {
				current.push({ type: 'video', url: embed.video });
			} else {
				current.push({ type: 'unknown' });
			}
		}
	}

	return { blocks, trailing: current };
}

function renderInline(tokens: InlineToken[]): ReactNode {
	const nodes: ReactNode[] = [];
	tokens.forEach((token, i) => {
		if (token.type === 'text') {
			nodes.push(<InlineRun key={i} text={token.text} attrs={token.attrs} />);
		} else if (token.type === 'image') {
			if (isSafeUrl(token.url, SAFE_EMBED_SCHEMES)) {
				nodes.push(<img key={i} src={token.url} alt="" />);
			}
		} else if (token.type === 'video') {
			if (isSafeUrl(token.url, SAFE_EMBED_SCHEMES)) {
				nodes.push(
					<iframe
						key={i}
						title="Embedded video"
						className="ql-video"
						frameBorder={0}
						allowFullScreen
						src={token.url}
					/>,
				);
			}
		}
	});
	return nodes;
}

function InlineRun({ text, attrs }: { text: string; attrs: InlineAttrs }) {
	let node: ReactNode = text;
	if (attrs.bold) {
		node = <strong>{node}</strong>;
	}
	if (attrs.italic) {
		node = <em>{node}</em>;
	}
	if (attrs.underline) {
		node = <u>{node}</u>;
	}
	if (attrs.size && ['small', 'large', 'huge'].includes(attrs.size)) {
		node = <span className={`ql-size-${attrs.size}`}>{node}</span>;
	}
	if (attrs.link && isSafeUrl(attrs.link, SAFE_LINK_SCHEMES)) {
		node = (
			<a href={attrs.link} target="_blank" rel="noopener noreferrer">
				{node}
			</a>
		);
	}
	return <>{node}</>;
}

function blockClassName(attrs: BlockAttrs): string | undefined {
	const classes: string[] = [];
	if (attrs.align && ['center', 'right', 'justify'].includes(attrs.align)) {
		classes.push(`ql-align-${attrs.align}`);
	}
	if (typeof attrs.indent === 'number' && attrs.indent >= 1 && attrs.indent <= 8) {
		classes.push(`ql-indent-${attrs.indent}`);
	}
	if (attrs.direction === 'rtl') {
		classes.push('ql-direction-rtl');
	}
	return classes.length > 0 ? classes.join(' ') : undefined;
}

function renderBlock(block: Block, key: number): ReactElement {
	const children = block.inline.length > 0 ? renderInline(block.inline) : <br />;
	const className = blockClassName(block.attrs);
	const header = block.attrs.header;
	if (typeof header === 'number' && header >= 1 && header <= 6) {
		const Tag = `h${header}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
		return (
			<Tag key={key} className={className}>
				{children}
			</Tag>
		);
	}
	return (
		<p key={key} className={className}>
			{children}
		</p>
	);
}

function renderListItem(block: Block, key: number): ReactElement {
	const listType = block.attrs.list ?? 'bullet';
	const className = blockClassName(block.attrs);
	return (
		<li key={key} data-list={listType} className={className}>
			<span className="ql-ui" contentEditable={false} />
			{block.inline.length > 0 ? renderInline(block.inline) : <br />}
		</li>
	);
}

export function renderDelta(delta: Delta | undefined): ReactNode {
	if (!delta || !delta.ops || delta.ops.length === 0) {
		return null;
	}
	const { blocks, trailing } = parseOps(delta.ops);
	// Quill's convention is that the Delta always ends with a trailing "\n".
	// If the document ends without one, flush any dangling inline tokens as a final paragraph.
	const allBlocks =
		trailing.length > 0 ? [...blocks, { attrs: {} as BlockAttrs, inline: trailing }] : blocks;

	const out: ReactElement[] = [];
	let i = 0;
	let keyCounter = 0;
	while (i < allBlocks.length) {
		const block = allBlocks[i];
		if (block.attrs.list) {
			const items: ReactElement[] = [];
			while (i < allBlocks.length && allBlocks[i].attrs.list) {
				items.push(renderListItem(allBlocks[i], keyCounter++));
				i += 1;
			}
			out.push(<ol key={keyCounter++}>{items}</ol>);
			continue;
		}
		out.push(renderBlock(block, keyCounter++));
		i += 1;
	}
	return out;
}
